import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import EyeIcon from "../../assets/icons/EyeIcon.js";
import NoDataIcon from "../../assets/icons/NoDataIcon.js";
import { stockTransferAPI } from "../../services";
import "../../style/transfer-history.css";

const TransferHistory = () => {
  const navigate = useNavigate();
  const { isPartsURL, isAccessoriesURL } = useSelector((state) => state.domain);
  
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  // Fetch transfers from API
  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const fetchTransfers = async () => {
      setLoading(true);
      try {
        const params = {
          page: currentPage,
          page_size: itemsPerPage,
        };

        if (searchTerm.trim()) {
          params.search = searchTerm.trim();
        }

        const response = await stockTransferAPI.getTransfers(params, {
          signal: controller.signal,
        });

        if (!isMounted) return;

        setTransfers(response.results || []);
        setTotalCount(response.count || 0);
      } catch (error) {
        if (!isMounted || error.name === "CanceledError") return;
        console.error("Failed to fetch transfers:", error);
        const message =
          error?.response?.data?.message ||
          error?.response?.data?.detail ||
          error?.message ||
          "Failed to load transfers";
        toast.error(message);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    const timeoutId = setTimeout(fetchTransfers, searchTerm ? 300 : 0);

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [currentPage, itemsPerPage, searchTerm]);

  const filteredTransfers = transfers;

  // Pagination
  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const currentTransfers = filteredTransfers;

  const handleViewDetails = (transferId) => {
    navigate(`/transfer-history/${transferId}`);
  };

  return (
    <div className="card rounded-4 p-4 border-0 transfer-history-container transfer-history-layout">
      <div className="transfer-history-top">
        {/* Header */}
        <div className="transfer-history-header">
          <div className="transfer-history-title">
            <h1>Transfer History</h1>
            <p>Check all the records of transfers to pos</p>
          </div>
        </div>

        {/* Search Bar */}
        <div className="d-flex align-items-center mb-3 gap-3">
          <div className="position-relative flex-grow-1">
            <input
              type="text"
              className={`${
                isPartsURL
                  ? "transfer-search-input-parts"
                  : isAccessoriesURL
                  ? "transfer-search-input-acc"
                  : "transfer-search-input"
              } text-search`}
              placeholder="Search by transfer no. or by model"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Search
              size={16}
              className="position-absolute top-50 start-0 translate-middle-y ms-3"
              style={{ color: "#8C959F" }}
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="transfer-history-table-container transfer-history-table-wrapper">
        <table className="transfer-history-table">
          <colgroup>
            <col style={{ width: "30%" }} />
            <col style={{ width: "30%" }} />
            <col style={{ width: "25%" }} />
            <col style={{ width: "15%" }} />
          </colgroup>
          <thead>
            <tr>
              <th>TRANSFER NO.</th>
              <th>MODEL</th>
              <th>QUANTITY</th>
              <th style={{ textAlign: "center" }}>ACTION</th>
            </tr>
          </thead>
        </table>
        <div className="transfer-history-table-body-wrapper">
          <table className="transfer-history-table">
            <colgroup>
              <col style={{ width: "30%" }} />
              <col style={{ width: "30%" }} />
              <col style={{ width: "25%" }} />
              <col style={{ width: "15%" }} />
            </colgroup>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="4" className="no-transfer-history-message">
                    <div
                      className=""
                      style={{
                        marginBottom: "4rem",
                        marginTop: "4rem",
                      }}
                    >
                      <div className="d-flex justify-content-center align-items-center">
                        <div
                          className="spinner-border text-primary"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </div>
                      <h3 style={{ marginTop: "2rem" }}>Loading transfers...</h3>
                    </div>
                  </td>
                </tr>
              ) : filteredTransfers.length === 0 ? (
                <tr>
                  <td colSpan="4" className="no-transfer-history-message">
                    <div
                      className=""
                      style={{
                        marginBottom: "4rem",
                        marginTop: "4rem",
                      }}
                    >
                      <NoDataIcon />
                      <h3 style={{ marginTop: "2rem" }}>No transfers found</h3>
                    </div>
                  </td>
                </tr>
              ) : (
                currentTransfers.map((transfer, index) => (
                  <tr
                    key={transfer.id}
                    style={{
                      fontWeight: "400",
                      fontSize: "14px",
                      letterSpacing: "0%",
                      color: "#57606A",
                      borderBottom: "1px solid #DCE3EB",
                    }}
                  >
                    {/* Transfer Number */}
                    <td
                      style={{
                        border: "none",
                        padding: "12px 16px",
                        borderBottom:
                          index === currentTransfers.length - 1
                            ? "none"
                            : "1px solid #DCE3EB",
                        borderBottomLeftRadius:
                          index === currentTransfers.length - 1 ? "8px" : "0",
                        color: "#57606A",
                      }}
                    >
                      {transfer.transfer_number}
                    </td>

                    {/* Model */}
                    <td
                      style={{
                        border: "none",
                        padding: "12px 16px",
                        borderBottom:
                          index === currentTransfers.length - 1
                            ? "none"
                            : "1px solid #DCE3EB",
                        color: "#57606A",
                      }}
                    >
                      {transfer.total_products || 0}
                    </td>

                    {/* Quantity */}
                    <td
                      style={{
                        border: "none",
                        padding: "12px 16px",
                        borderBottom:
                          index === currentTransfers.length - 1
                            ? "none"
                            : "1px solid #DCE3EB",
                        color: "#57606A",
                      }}
                    >
                      {transfer.total_quantity || 0}
                    </td>

                    {/* Action buttons */}
                    <td>
                      <div className="action-buttons">
                        <button
                          className={`${
                            isPartsURL
                              ? "action-btn-parts view"
                              : isAccessoriesURL
                              ? "action-btn-acc view"
                              : "action-btn view"
                          }`}
                          onClick={() => handleViewDetails(transfer.id)}
                          title="View"
                        >
                          <EyeIcon
                            fill={
                              isPartsURL
                                ? "var(--primary-color-parts)"
                                : isAccessoriesURL
                                ? "var(--primary-color-acc)"
                                : "var(--primary-color-mobile)"
                            }
                          />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-4 transfer-history-pagination-sticky">
          <div className="mrph-txt size-14 color-text-semi-regular">
            Page {currentPage} of {totalPages}
          </div>
          <div className="pagination-controls">
            <button
              className={`${
                isPartsURL
                  ? "pagination-btn-parts"
                  : isAccessoriesURL
                  ? "pagination-btn-acc"
                  : "pagination-btn"
              }`}
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            >
              Previous
            </button>
            <button
              className={`${
                isPartsURL
                  ? "pagination-btn-next-parts"
                  : isAccessoriesURL
                  ? "pagination-btn-next-acc"
                  : "pagination-btn-next"
              }`}
              disabled={currentPage >= totalPages}
              onClick={() =>
                setCurrentPage((prev) => Math.min(totalPages, prev + 1))
              }
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransferHistory;

